import { useFormikContext } from "formik";
import React, { useEffect } from "react";

import { useStoreServiceApi } from "@api/hooks/storeService.hook";
import { ShopName } from "@api/hooks/storeService.types";
import { DefaultFor } from "@api/mappers/default.mapper";

import { PreferredStoreService } from "@services/preferredStore.service";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { default as SelectField } from "@components/Form/FormFields/SelectField/selectField.component";

type Props = {
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    autoComplete?: boolean;
    microCopy?: any;
    onChange?: any;
    validationRule?: any;
    optionalIndication?: boolean;
    excludeStores?: Array<string>;
    disabled?: boolean;
};

const StoreField = ({
    name,
    label,
    placeholder,
    required,
    autoComplete,
    microCopy,
    onChange,
    validationRule,
    optionalIndication,
    excludeStores,
    disabled,
}: Props) => {
    const [{ data, loading }] = useStoreServiceApi({
        url: "/localized/stores/shopnames",
        method: "GET",
        mapper: DefaultFor<Array<ShopName>>(),
    });
    const { values, setFieldValue } = useFormikContext<any>();
    const { t } = useGranite();

    useEffect(() => {
        if (data && data.length > 0) {
            const currentData = values[name];
            let find = data.find(store => store.code === currentData);
            if (!find) {
                // invalid storeId (ex: store is permanently closed)
                setFieldValue(name, "");
                setFieldValue(`${name}WasInvalid`, true);
                PreferredStoreService.cleanUpPreferredStores();
            }
        }
    }, [data]);

    let storeList =
        data?.map(store => {
            if (store?.shopName) {
                return {
                    label: store?.shopName,
                    value: store.code,
                };
            }
        }) || [];

    if (excludeStores && excludeStores.length > 0) {
        storeList = storeList.filter(
            store => !excludeStores.some(s => store?.value === s),
        );
    }

    if (storeList.length === 0 && !loading) {
        return null;
    }

    return (
        <SelectField
            name={name}
            label={label}
            placeholder={placeholder}
            autoComplete={autoComplete}
            microCopy={microCopy}
            onChange={onChange}
            required={required}
            optionList={[
                {
                    label: t("general.form.store.default.entry"),
                    value: "",
                },
                ...storeList,
            ]}
            validationRule={validationRule}
            optionalIndication={optionalIndication}
            disabled={disabled}
        />
    );
};

export { StoreField };
